import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import DataTable from "ui/DataTable"
import styles from "./index.module.css"
import CustomPagination from "components/common/CustomPagination"
import { getSignedDocuments } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { Modal, List } from "antd"

const SignedDocumentsTable = ({ searchValue, selectedExperiences }) => {
  const dispatch = useDispatch()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [sortParams, setSortParams] = useState({})
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedWaiverNames, setSelectedWaiverNames] = useState([])
  const { id } = useSelector((store) => store?.user.guide) || {}
  const { items, loading, metadata } = useSelector((state) => state.digitalWaiver.signedDocuments)
  const parseWaiverNames = (waiverName) =>
    waiverName?.split(",")?.map((name) => name.trim())?.filter((name) => name) || []

  const handleRowClick = (record) => {
    console.log("Row clicked:", record)
    // Add your row click logic here
  }

  const handleWaiverNamesClick = (waiverName) => {
    setSelectedWaiverNames(parseWaiverNames(waiverName))
    setModalVisible(true)
  }

  const handleModalClose = () => {
    setModalVisible(false)
    setSelectedWaiverNames([])
  }

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current: page
    }))
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const newSortParams = {}
    if (sorter?.field) {
      newSortParams[sorter.field] = sorter.order === "ascend" ? "asc" : "desc"
    }
    setSortParams(newSortParams)
    setPagination((prev) => ({
      ...prev,
      current: 1 // Reset to first page when sorting
    }))
  }

  // Fetch data when page, page size, search value, selected experiences, or sort params changes
  useEffect(() => {
    const experienceIds = selectedExperiences.length > 0 ? selectedExperiences : null
    const sortField = Object.keys(sortParams)[0]
    const sortOrder = sortParams[sortField]
    dispatch(getSignedDocuments(id, pagination.current, pagination.pageSize, searchValue, experienceIds, sortField, sortOrder))
  }, [dispatch, pagination.current, pagination.pageSize, searchValue, selectedExperiences, sortParams])

  const columns = [
    {
      title: "Experience Date & Time",
      key: "dateTime",
      dataIndex: "date",
      sorter: true,
      render: (_, record) => {
        const date = new Date(record.date)
        const formattedDate = date.toLocaleDateString()
        return `${formattedDate} | ${record.start_time}`
      }
    },
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      key: "guest_name",
      sorter: true
    },
    {
      title: "Experience Name",
      dataIndex: "experience_name",
      key: "experience_name",
      sorter: true,
      render: (experienceName) => (
        <span title={experienceName}>{experienceName?.length > 15 ? `${experienceName.substring(0, 12)}...` : experienceName}</span>
      )
    },
    {
      title: "Booking ID",
      dataIndex: "booking_id",
      key: "booking_id",
      sorter: true,
      render: (bookingId) => <span className={styles.bookedId}>{bookingId}</span>
    },
    {
      title: "Waiver Name",
      dataIndex: "waiver_name",
      key: "waiver_name",
      render: (waiverName) => {
        if (!waiverName) return "N/A"

        const waiverNames = parseWaiverNames(waiverName)
        const firstName = waiverNames[0]
        const count = waiverNames.length

        if (count === 1) {
          return firstName
        }

        return (
          <span>
            {firstName}
            <span
              className={styles.waiverCount}
              onClick={() => handleWaiverNamesClick(waiverName)}
              style={{ color: "var(--color-primary)", cursor: "pointer", marginLeft: "4px" }}
            >
              +{count - 1}
            </span>
          </span>
        )
      }
    },
    {
      title: "Status",
      dataIndex: "waiver_status",
      key: "waiver_status",
      render: (status) => {
        const statusClass = status === "Signed" ? styles.statusSigned : styles.statusUnsigned
        return <span className={statusClass}>{status || "Unsigned"}</span>
      }
    }
  ]
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      current: 1 // Reset to first page when page size changes
    }))
  }

  return (
    <div>
      <DataTable
        columns={columns}
        dataSource={items}
        rowKey="key"
        // rowClickKey="booking_id"
        onRowClick={handleRowClick}
        onChange={handleTableChange}
        pagination={false}
        loading={loading}
        // rowClassName={styles.tableRow}
      />
      <div className="mt-4">
        <CustomPagination
          metadata={metadata}
          pageSize={pagination.pageSize}
          total={metadata?.total_results || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <Modal
        title="All Waiver Names"
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={400}
        centered
        maskClosable={false}
      >
        <List dataSource={selectedWaiverNames} renderItem={(name, index) => <List.Item key={index}>{name}</List.Item>} />
      </Modal>
    </div>
  )
}

export default SignedDocumentsTable

SignedDocumentsTable.propTypes = {
  searchValue: PropTypes.string,
  selectedExperiences: PropTypes.array
}
